import { useState, useCallback } from 'react';
import * as XLSX from 'xlsx';
import UploadSection from './components/UploadSection';
import ConfigSection from './components/ConfigSection';
import GenerateSection from './components/GenerateSection';
import ResultsSection from './components/ResultsSection';
import QuantityModal from './components/QuantityModal';
import EmailStatusToast, { EmailToastStatus } from './components/EmailStatusToast';
import { sendListsEmail } from '../../services/emailService';
import { downloadListsAsExcel } from '../../services/excelService';

export interface Player {
  numero: number;
  jugador1: string;
  jugador2: string;
  jugador3: string;
  club: string;
  categoria: string;
}

export type Modality = 'individuales' | 'duplas' | 'trios' | 'cuartetos';

export interface GeneratedList {
  listNumber: number;
  players: Player[];
  type: 'individual' | 'dupla' | 'trio';
}

export default function Home() {
  const [players, setPlayers] = useState<Player[]>([]);
  const [generatedLists, setGeneratedLists] = useState<GeneratedList[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [emailStatus, setEmailStatus] = useState<EmailToastStatus>(null);

  const getModalidad = (p: Player): 'individual' | 'dupla' | 'trio' => {
    if (p.jugador3?.trim()) return 'trio';
    if (p.jugador2?.trim()) return 'dupla';
    return 'individual';
  };

  const individualesCount = players.filter(p => getModalidad(p) === 'individual').length;
  const duplasCount = players.filter(p => getModalidad(p) === 'dupla').length;
  const triosCount = players.filter(p => getModalidad(p) === 'trio').length;

  const handleFileUpload = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const data = e.target?.result;
      const workbook = XLSX.read(data, { type: 'binary' });
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      const jsonData = XLSX.utils.sheet_to_json(worksheet) as any[];

      const normalizeKey = (key: string) =>
        key
          .toLowerCase()
          .trim()
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .replace(/\s+/g, '');

      const parsedPlayers: Player[] = jsonData.map((row, index) => {
        const normalizedRow: Record<string, any> = {};
        Object.keys(row).forEach((key) => {
          normalizedRow[normalizeKey(key)] = row[key];
        });
        return {
          numero: normalizedRow['numero'] ?? index + 1,
          jugador1: String(normalizedRow['jugador1'] || ''),
          jugador2: String(normalizedRow['jugador2'] || ''),
          jugador3: String(normalizedRow['jugador3'] || ''),
          club: String(normalizedRow['club'] || ''),
          categoria: String(normalizedRow['categoria'] || ''),
        };
      });

      setPlayers(parsedPlayers);
      setGeneratedLists([]);
    };
    reader.readAsBinaryString(file);
  };

  // Distribuye jugadores separando clubes lo más posible
  const distributeWithClubSeparation = (group: Player[]): Player[] => {
    const byClub: Record<string, Player[]> = {};
    group.forEach((p) => {
      const key = p.club.trim() || '(sin club)';
      if (!byClub[key]) byClub[key] = [];
      byClub[key].push({ ...p });
    });

    Object.values(byClub).forEach((arr) => {
      for (let i = arr.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [arr[i], arr[j]] = [arr[j], arr[i]];
      }
    });

    const queues = Object.values(byClub).map((arr) => [...arr]);
    for (let i = queues.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [queues[i], queues[j]] = [queues[j], queues[i]];
    }

    const interleaved: Player[] = [];
    let hasMore = true;
    let round = 0;
    while (hasMore) {
      hasMore = false;
      const shuffledIdx = queues.map((_, i) => i).sort(() => Math.random() - 0.5);
      for (const ci of shuffledIdx) {
        if (round < queues[ci].length) {
          interleaved.push(queues[ci][round]);
          hasMore = true;
        }
      }
      round++;
    }
    return interleaved;
  };

  // Distribuye los jugadores en listas greedy separando clubes
  const buildListsForType = (
    group: Player[],
    perList: number,
    type: 'individual' | 'dupla' | 'trio',
    startNumber: number
  ): GeneratedList[] => {
    const interleaved = distributeWithClubSeparation(group);
    const lists: GeneratedList[] = [];
    const used = new Set<number>();
    let listNumber = startNumber;

    while (used.size < interleaved.length) {
      const listItems: Player[] = [];
      const clubsInList = new Set<string>();

      // Primera pasada: clubes distintos
      for (let i = 0; i < interleaved.length && listItems.length < perList; i++) {
        if (!used.has(i) && !clubsInList.has(interleaved[i].club)) {
          listItems.push(interleaved[i]);
          clubsInList.add(interleaved[i].club);
          used.add(i);
        }
      }

      // Segunda pasada: completar si faltan lugares
      for (let i = 0; i < interleaved.length && listItems.length < perList; i++) {
        if (!used.has(i)) {
          listItems.push(interleaved[i]);
          used.add(i);
        }
      }

      if (listItems.length > 0) {
        lists.push({ listNumber, players: listItems, type });
        listNumber++;
      }
    }

    return lists;
  };

  const generateLists = useCallback(async (quantities: { individuales: number; duplas: number; trios: number }) => {
    if (players.length === 0) return;

    const result: GeneratedList[] = [];

    const individualesPlayers = players.filter(p => getModalidad(p) === 'individual');
    const duplasPlayers = players.filter(p => getModalidad(p) === 'dupla');
    const triosPlayers = players.filter(p => getModalidad(p) === 'trio');

    let nextNumber = 1;

    if (individualesPlayers.length > 0 && quantities.individuales > 0) {
      const indLists = buildListsForType(individualesPlayers, quantities.individuales, 'individual', nextNumber);
      result.push(...indLists);
      nextNumber += indLists.length;
    }

    if (duplasPlayers.length > 0 && quantities.duplas > 0) {
      const dupLists = buildListsForType(duplasPlayers, quantities.duplas, 'dupla', nextNumber);
      result.push(...dupLists);
      nextNumber += dupLists.length;
    }

    if (triosPlayers.length > 0 && quantities.trios > 0) {
      const triLists = buildListsForType(triosPlayers, quantities.trios, 'trio', nextNumber);
      result.push(...triLists);
    }

    setGeneratedLists(result);
    setShowModal(false);

    // Descargar Excel automáticamente
    const pad = (n: number) => String(n).padStart(2, '0');
    const now = new Date();
    const dateTag = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}_${pad(now.getHours())}${pad(now.getMinutes())}`;
    downloadListsAsExcel(result, `listas_generadas_${dateTag}.xlsx`);

    // Enviar email automáticamente
    setEmailStatus('sending');
    const emailResult = await sendListsEmail(result);
    if (emailResult.success) {
      setEmailStatus('success');
    } else if (emailResult.error === 'EMAIL_NOT_CONFIGURED') {
      setEmailStatus('not_configured');
    } else {
      setEmailStatus('error');
    }
  }, [players]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center gap-4">
            <img
              src="https://static.readdy.ai/image/9c7e91af1709a924dbdc05462ce9f269/da0269138c27ad40787582f8537055a8.png"
              alt="Logo"
              className="h-12 w-auto object-contain"
            />
            <h1 className="text-2xl font-bold text-gray-900">Sistema de Generación de Listas</h1>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="space-y-8">
          {/* Upload Section */}
          <UploadSection
            onFileUpload={handleFileUpload}
            individuales={individualesCount}
            duplas={duplasCount}
            trios={triosCount}
          />

          {/* Config Section */}
          {players.length > 0 && (
            <ConfigSection players={players} maxPerList={10} />
          )}

          {/* Generate Section - siempre visible */}
          <GenerateSection
            onGenerate={() => setShowModal(true)}
            disabled={players.length === 0}
          />

          {/* Results Section */}
          {generatedLists.length > 0 && (
            <ResultsSection lists={generatedLists} />
          )}
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-100 mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <p className="text-center text-sm text-gray-500">
            Sistema de Generación de Listas de Jugadores © 2025
          </p>
        </div>
      </footer>

      {/* Quantity Modal */}
      <QuantityModal
        isOpen={showModal}
        individualesCount={individualesCount}
        duplasCount={duplasCount}
        triosCount={triosCount}
        onConfirm={generateLists}
        onCancel={() => setShowModal(false)}
      />

      {/* Email Status Toast */}
      <EmailStatusToast
        status={emailStatus}
        onClose={() => setEmailStatus(null)}
      />
    </div>
  );
}
